import type { ReactElement, ReactNode } from 'react';
import { isValidElement, Suspense, useRef, useState } from 'react';
import { useFocus, useFocusVisible, useHover } from 'react-aria';
import { useLocation, useMatches } from 'react-router';
import { enableNewNavigation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useTextOverflow } from '@gonfalon/dom/useTextOverflow';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { msg } from '@gonfalon/messages/msg';
import { isMessageRef, type MessageRef } from '@gonfalon/messages/ref';
import { matchProjectSettingsFullScreenFlow } from '@gonfalon/navigator/matchers';
import { toHref, toUnifiedSettings } from '@gonfalon/navigator/navigators';
import {
  Breadcrumb as Breadcrumb_,
  Breadcrumbs as Breadcrumbs_,
  Link,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import clsx from 'clsx';
import { z } from 'zod';

import { ProjectBreadcrumbSwitcher, ProjectSwitcherSkeleton } from '../../ProjectSwitcher';
import { useSidebarContext } from '../../Sidebar';
import { isFlattenedSettingsEnabled } from '../SettingsNavigation/isFlattenedSettingsEnabled';
import { trackSettingsBreadcrumbClicked } from '../SettingsNavigation/settingsAnalytics';
import { useIsFlatProjectSettingsSurface } from '../SettingsNavigation/useIsFlatProjectSettingsSurface';

import styles from './Breadcrumbs.module.css';

const reactElementSchema = z.custom<ReactElement>((val) => isValidElement(val), {
  message: 'Expected a React element',
});

const breadcrumbReturnSchema = z.union([z.string(), reactElementSchema, z.null(), z.undefined()]);

// A route can supply its breadcrumb as a callback taking the route's loader data. Validating the
// handle only checks that the value is callable; the value the callback returns is checked each
// time it runs, and the loader data is handed to it untouched.
const breadcrumbFunctionSchema = z.function({
  input: [z.custom<{ data: unknown }>()],
  output: breadcrumbReturnSchema,
});

// A handle can also point at the copy catalog with messageRef(); the id is resolved here, inside
// the provider, because a handle is evaluated at module scope where msg() cannot run.
const messageRefSchema = z.custom<MessageRef>(isMessageRef);

const breadcrumbValueSchema = z.union([z.string(), messageRefSchema, breadcrumbFunctionSchema, reactElementSchema]);

const breadcrumbSchema = z.object({ breadcrumb: breadcrumbValueSchema });

const flatSettingsBreadcrumbSchema = z.object({
  flatSettings: z.object({
    breadcrumb: breadcrumbValueSchema,
    scope: z.literal('organization').optional(),
    leading: z.array(reactElementSchema).optional(),
  }),
});

type Item = {
  // id is used by react-aria-components under the hood
  id: string;
  text?: string | ReactNode;
  href: string;
  onPress?: () => void;
};

export function Breadcrumbs() {
  const matches = useMatches();
  const location = useLocation();
  const sidebar = useSidebarContext();

  const isFlatSettings = isFlattenedSettingsEnabled() && !matchProjectSettingsFullScreenFlow(location);
  const isFlatProjectSettingsSurface = useIsFlatProjectSettingsSurface();
  const flatOverrides = isFlatSettings
    ? matches.flatMap((match) => {
        const parsed = flatSettingsBreadcrumbSchema.safeParse(match.handle);
        return parsed.success ? [parsed.data.flatSettings] : [];
      })
    : [];
  const leadingOverride = flatOverrides.findLast((override) => override.leading)?.leading;
  const isOrganizationSettings = flatOverrides.some((override) => override.scope === 'organization');

  const crumbs = matches.reduce((acc, match) => {
    const override = isFlatSettings ? flatSettingsBreadcrumbSchema.safeParse(match.handle) : undefined;
    const parse = breadcrumbSchema.safeParse(match.handle);

    let breadcrumb: z.infer<typeof breadcrumbValueSchema>;
    if (override?.success) {
      breadcrumb = override.data.flatSettings.breadcrumb;
    } else if (parse.success) {
      breadcrumb = parse.data.breadcrumb;
    } else {
      return acc;
    }

    let text;

    if (isMessageRef(breadcrumb)) {
      text = msg(breadcrumb.messageId);
    } else if (typeof breadcrumb === 'function') {
      if (!match.loaderData) {
        return acc;
      }
      text = breadcrumb({ data: match.loaderData });
    } else {
      text = breadcrumb;
    }

    if (text === null || text === undefined || text === '') {
      return acc;
    }

    acc.push({
      id: match.id,
      text,
      href: match.pathname,
    });
    return acc;
  }, new Array<Item>());

  let leading: ReactElement[] | undefined;
  if (isFlatSettings) {
    if (leadingOverride) {
      leading = leadingOverride;
    } else if (isFlatProjectSettingsSurface) {
      leading = enableNewNavigation()
        ? []
        : [
            <Suspense key="project" fallback={<ProjectSwitcherSkeleton />}>
              <ProjectBreadcrumbSwitcher />
            </Suspense>,
          ];
    } else if (isOrganizationSettings) {
      leading = [];
    }
  }

  const items = leading
    ? [
        {
          id: 'flat-settings',
          text: msg('chrome.settingsNavigation.title'),
          href: toHref(toUnifiedSettings()),
          onPress: () => trackSettingsBreadcrumbClicked({ crumb: 'settings', fromPath: location.pathname }),
        },
        ...leading.map((element, index) => ({ id: `flat-settings-leading-${index}`, text: element, href: '' })),
        ...crumbs,
      ]
    : crumbs;

  return (
    <QuietErrorBoundary severity="medium">
      <Breadcrumbs_
        items={items}
        className={clsx(styles.breadcrumbs, {
          [styles.withSidebar]: sidebar.isVisible,
          [styles.newNavigation]: enableNewNavigation(),
        })}
      >
        {(item) => <Breadcrumb {...item} />}
      </Breadcrumbs_>
    </QuietErrorBoundary>
  );
}

const MAX_CHARS = 64;

function Breadcrumb(item: Item) {
  const { href, text, onPress } = item;

  const innerRef = useRef<HTMLSpanElement>(null);
  const { ref, hasOverflow, props: overflowProps } = useTextOverflow<HTMLSpanElement>();
  const { hoverProps, isHovered } = useHover({});
  const [isFocused, setFocused] = useState(false);
  const { focusProps } = useFocus({
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  });
  const { isFocusVisible } = useFocusVisible();

  if (isValidElement(text)) {
    return <Breadcrumb_ className={clsx(styles.crumb, styles.shrink)}>{text}</Breadcrumb_>;
  }

  return (
    <Breadcrumb_
      className={clsx(styles.crumb, {
        [styles.shrink]: typeof text === 'string' && text.length < MAX_CHARS,
      })}
    >
      <TooltipTrigger isDisabled={!hasOverflow} isOpen={hasOverflow && (isHovered || (isFocusVisible && isFocused))}>
        {/* @ts-expect-error react-aria-components and react-aria are in disagreement about the event type here, but it's legit. */}
        <Link href={href} onPress={onPress} {...overflowProps} {...focusProps} ref={ref}>
          <span ref={innerRef} {...hoverProps}>
            {text}
          </span>
        </Link>
        <Tooltip className={styles.itemText} triggerRef={ref} placement="bottom start">
          {text}
        </Tooltip>
      </TooltipTrigger>
    </Breadcrumb_>
  );
}
